/**
 * Operator-only starting point for a clean, realistic onboarding walk-through.
 * The server remains the authority: switching and erasing both require Jeremy's
 * membership and the private Test Account allow-list.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useAccountChange } from "../lib/account-context.js";

const TEST_ACCOUNT_ID = "22222222-2222-4222-8222-222222222222";

export default function TestOnboardingPage(): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const [error, setError] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: async () => {
      setError(null);
      await changeAccount({
        path: "/account/switch",
        body: { accountId: TEST_ACCOUNT_ID },
        targetAccountId: TEST_ACCOUNT_ID,
        destination: "/welcome",
        afterRebind: async () => {
          await api.post("/account/reset-onboarding", {});
        },
      });
    },
    onError: () => setError("We could not start the test. No client account was changed. Check the Test Account, then try again."),
  });
  const openPart = useMutation({
    mutationFn: async ({ step, url }: { step: number; url: string }) => {
      setError(null);
      await changeAccount({
        path: "/account/switch",
        body: { accountId: TEST_ACCOUNT_ID },
        targetAccountId: TEST_ACCOUNT_ID,
        destination: url,
        afterRebind: async () => {
          const current = await api.get<{ journey: { currentStep: number } }>("/onboarding/journey");
          await api.patch("/onboarding/journey", { currentStep: Math.max(current.journey.currentStep, step) });
        },
      });
    },
    onError: () => setError("We could not open that part of the test. Nothing was erased. Try it again."),
  });

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Test the full client onboarding</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, lineHeight: 1.5, margin: 0, maxWidth: 690 }}>
        This starts with the same empty setup a new client sees after they accept their invite. It only clears the private Test Account. Your public demo and every client account stay exactly as they are.
      </p>

      <section style={{ marginTop: 22, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: "20px" }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>What you can test</h2>
        <ul style={{ margin: "12px 0 18px", paddingLeft: 20, color: "var(--body)", lineHeight: 1.65, fontSize: 13.5 }}>
          <li>The welcome questions, connections, and file-upload steps</li>
          <li>The import check and the Profit Playground</li>
          <li>Product setup, costs, contribution margin, overhead, and verification</li>
        </ul>
        <div style={{ padding: "11px 13px", borderRadius: "var(--r-sm)", background: "color-mix(in srgb, var(--accent) 7%, var(--surface))", border: "1px solid color-mix(in srgb, var(--accent) 24%, var(--border))", color: "var(--body)", fontSize: 13, lineHeight: 1.5 }}>
          After you choose your data sources, the Test Account loads realistic sample connections, products, and numbers automatically. You do not need any real account logins.
        </div>
        <button
          type="button"
          onClick={() => start.mutate()}
          disabled={start.isPending}
          style={{ marginTop: 18, border: "none", borderRadius: "var(--r-sm)", background: "var(--accent)", color: "var(--accent-contrast)", padding: "10px 16px", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: start.isPending ? "wait" : "pointer", opacity: start.isPending ? 0.65 : 1 }}
        >
          {start.isPending ? "Starting your test…" : "Start fresh Test Account onboarding"}
        </button>
        {error && <p role="alert" style={{ margin: "12px 0 0", color: "var(--bad)", fontSize: 13, lineHeight: 1.45 }}>{error}</p>}
      </section>

      <section style={{ marginTop: 18, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: "20px" }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>Open one part without starting over</h2>
        <p style={{ margin: "8px 0 16px", color: "var(--body)", fontSize: 13.5, lineHeight: 1.5 }}>
          This keeps every saved answer and number in the Test Account. Pick the exact place you want to review.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          <PartButton title="Choose and connect data" body="Review platform choices, connections, and files." busy={openPart.isPending} onClick={() => openPart.mutate({ step: 2, url: "/welcome?step=2" })} />
          <PartButton title="Review product setup" body="Open the saved product, variant, bundle, and code organization." busy={openPart.isPending} onClick={() => openPart.mutate({ step: 6, url: "/onboarding/chat?stage=categorize&from=guided&review=1" })} />
          <PartButton title="Review costs and margins" body="Open the final cost review with every product ready to confirm again." busy={openPart.isPending} onClick={() => openPart.mutate({ step: 6, url: "/onboarding/chat?stage=review&from=guided&resetReview=1" })} />
          <PartButton title="Continue after products and costs" body="Start with the trust check and see what happens next." busy={openPart.isPending} onClick={() => openPart.mutate({ step: 7, url: "/welcome?step=7" })} />
        </div>
      </section>
    </div>
  );
}

function PartButton({ title, body, busy, onClick }: { title: string; body: string; busy: boolean; onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      style={{ textAlign: "left", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", background: "var(--surface-2)", color: "var(--text)", padding: "14px", cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.65 : 1 }}
    >
      <span style={{ display: "block", fontSize: 13.5, fontWeight: 750 }}>{title}</span>
      <span style={{ display: "block", marginTop: 5, color: "var(--body)", fontSize: 12.5, lineHeight: 1.45 }}>{body}</span>
    </button>
  );
}
